// @flow
import React, { PureComponent } from "react";
import { Manager } from "react-popper";
import FormsyEditorBase from "./FormsyEditorBase";
import dayjs from "dayjs";
import LocalDate from "containers/LocalDate";
import { customParseDate } from "data/utils";
import DayPickerInput from "components/DayPickerInput";
import { DateTimeFormat, type DateTimeFormatValues } from "data/types";
import ClearableInput from "@hs/clearable-input";

type Props = {
    /** Field name */
    name: string,
    /** Field value */
    value: ?string,
    /** whether this field is required */
    isRequired: boolean,
    /** whether this field is readonly */
    isReadonly: boolean,
    /** how to render component */
    type: DateTimeFormatValues,
    /** whether to autofocus the field or not */
    autoFocus: boolean,
};

type State = {
    day: Date,
    time: Date,
    dateValue: string,
    timeValue: string,
};
export class DateEditorControl extends PureComponent<Props, State> {
    format: string;

    static defaultProps = {
        value: "",
        isRequired: false,
        isReadonly: false,
        type: DateTimeFormat.Date,
    };

    constructor(props: Props) {
        super(props);
        // full date&time format
        this.format = dayjs.format(props.type);
        this.timeFormat = props.type.toLowerCase().includes("long")
            ? "HH:mm:ss"
            : "HH:mm";
        // parse pristine value or set NOW
        if (props.value != null && String(props.value).length > 0) {
            const dt = customParseDate(props.value, this.format);

            this.state = {
                day: dt.toDate(),
                time: dt.toDate(),
                dateValue: dt.format("L"),
                timeValue: dt.format(this.timeFormat),
            };
        } else {
            this.state = { day: new Date(), time: new Date() };
        }
    }

    componentDidUpdate(prevProps: Props) {
        if (
            prevProps.value !== this.props.value &&
            !this.props.value?.startsWith?.("__INVALID__")
        ) {
            if (this.props.value) {
                const dt = customParseDate(this.props.value, this.format);
                this.setState({
                    day: dt.toDate(),
                    time: dt.toDate(),
                    dateValue: dt.format("L"),
                    timeValue: dt.format(this.timeFormat),
                });
            } else {
                this.setState({
                    dateValue: undefined,
                    timeValue: undefined,
                });
            }
        }
    }

    _getDate = () => {
        const { day, time } = this.state;
        const m = this.shouldRenderTime()
            ? dayjs(
                  // only consider the time for the date if it is valid
                  new Date(
                      day.getFullYear(),
                      day.getMonth(),
                      day.getDate(),
                      time.getHours(),
                      time.getMinutes(),
                      time.getSeconds()
                  )
              )
            : /* istanbul ignore next */
              dayjs(new Date(day.getFullYear(), day.getMonth(), day.getDate()));
        return m.format(this.format);
    };

    _onDayChange = (
        day: ?Date,
        dayPickerInput: DayPickerInput,
        changeValue: Function,
        inputValue: ?Date,
        isValueEmpty: boolean = false
    ) => {
        // this callback is called before the setState is finished,
        // so when the user picks a value from the date picker
        // we have to directly pass it to the function
        // otherwise we can use the value in the input field

        const value =
            inputValue ||
            dayPickerInput?._ref?.value ||
            dayPickerInput?.state?.value;

        if (day != null) {
            this.setState(
                {
                    day,
                    dateValue: dayjs(day).format("L"),
                },
                () =>
                    changeValue({
                        currentTarget: {
                            value:
                                !this.shouldRenderTime() ||
                                this.getValueAsDate(this.state.timeValue) !== ""
                                    ? this._getDate()
                                    : this.invalidate(this.state.dateValue),
                        },
                    })
            );
        } else {
            this.setState((prevState) => {
                const dateValue = this.invalidate(value);
                const nextState = {
                    dateValue,
                    timeValue: prevState.timeValue,
                };
                // Clear was pressed
                if (
                    prevState.dateValue === value ||
                    prevState.dateValue === dateValue ||
                    value === "" ||
                    isValueEmpty
                ) {
                    nextState.dateValue = this.shouldRenderTime()
                        ? this.invalidate()
                        : null;
                    if (prevState.timeValue === "__INVALID__") {
                        nextState.time = new Date();
                        nextState.timeValue = null;
                    }
                    if (nextState.timeValue == null) nextState.dateValue = null;
                }
                changeValue({
                    currentTarget: {
                        value: nextState.dateValue,
                    },
                });
                return nextState;
            });
        }
    };

    _onTimeChange = (event: SyntheticInputEvent<*>, changeValue: Function) => {
        const value = event.currentTarget.value;
        const time = dayjs(value, this.timeFormat).toDate();
        if (
            value?.trim().length > 0 &&
            !isNaN(time) &&
            (!this.shouldRenderSeconds() || value.match(/:/g).length === 2) //HACK: do validate long time correctly
        ) {
            this.setState({ time, timeValue: value }, () =>
                changeValue({
                    currentTarget: {
                        value:
                            !this.shouldRenderDate() ||
                            this.getValueAsDate(this.state.dateValue) !== ""
                                ? this._getDate()
                                : this.invalidate(value),
                    },
                })
            );
        } else {
            this.setState((prevState) => {
                const timeValue = this.invalidate(value);
                const nextState = {
                    timeValue,
                    dateValue: prevState.dateValue,
                };
                // Clear was pressed
                if (value === "") {
                    nextState.timeValue = this.shouldRenderDate()
                        ? this.invalidate()
                        : null;
                    // Handle date with time invalidation
                    if (prevState.dateValue === "__INVALID__")
                        nextState.dateValue = null;
                    if (nextState.dateValue == null) nextState.timeValue = null;
                }
                changeValue({
                    currentTarget: {
                        value: nextState.timeValue,
                    },
                });
                return nextState;
            });
        }
    };

    shouldRenderDate = () => this.props.type.toLowerCase().includes("date");
    shouldRenderTime = () => this.props.type.toLowerCase().includes("time");
    shouldRenderSeconds = () => this.props.type.toLowerCase().includes("long");

    invalidate = (value: string = ""): string => `__INVALID__${value}`;

    /* istanbul ignore next */
    getValueAsDate = (
        value: ?string,
        format: string = "L",
        valueFormat: string = this.format
    ) =>
        value &&
        String(value).length > 0 &&
        !String(value).startsWith("__INVALID__")
            ? dayjs(value, valueFormat).format(format)
            : "";

    getValidValue = (value: ?string): any =>
        value && typeof value === "string"
            ? value.replace("__INVALID__", "")
            : value;

    render = () => (
        <FormsyEditorBase
            required={
                this.props.isRequired ? "isDefaultRequiredValue" : undefined
            }
            render={(
                value: ?string,
                changeValue: (name: string, value: ?string) => void,
                isValid: boolean
            ) => (
                <Manager>
                    {this.shouldRenderDate() && (
                        <DayPickerInput
                            inputProps={{
                                className: `form-control${
                                    !isValid ? " has-error" : ""
                                }`,
                                disabled: this.props.isReadonly,
                            }}
                            required={this.props.isRequired}
                            classNames={{
                                container:
                                    "DayPickerInput container-form-control",
                                overlayWrapper: "DayPickerInput-OverlayWrapper",
                                overlay: "DayPickerInput-Overlay",
                            }}
                            dayPickerProps={{
                                showWeekNumber: true,
                                numberOfMonths: 1,
                                fixedWeeks: true,
                            }}
                            value={this.getValidValue(this.state.dateValue)}
                            id={this.props.name}
                            onDayChange={
                                /* istanbul ignore next */ (
                                    day: ?Date,
                                    dayPickerInput: DayPickerInput,
                                    inputValue: ?Date,
                                    isValueEmpty: ?boolean
                                ) =>
                                    this._onDayChange(
                                        day,
                                        dayPickerInput,
                                        changeValue,
                                        inputValue,
                                        isValueEmpty
                                    )
                            }
                            placeholder={dayjs.localeData().longDateFormat("L")}
                            format={dayjs.localeData().longDateFormat("L")}
                            autoFocus={this.props.autoFocus}
                        />
                    )}
                    {this.shouldRenderTime() && (
                        <ClearableInput
                            onChangeWithRawEvent={true}
                            name={this.props.name}
                            inputType="time"
                            disabled={this.props.isReadonly}
                            value={this.getValidValue(this.state.timeValue)}
                            step={this.shouldRenderSeconds() ? 1 : 60}
                            onChange={
                                /*istanbul ignore next*/ (event) =>
                                    this._onTimeChange(event, changeValue)
                            }
                            style={{
                                flexGrow: 0,
                            }}
                            className="TimePickerInput form-control"
                        />
                    )}
                </Manager>
            )}
            {...this.props}
        />
    );
}

export default LocalDate(DateEditorControl);
